import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import { MANUAL_KEYS } from '../reportingFollowUpConstants';
import {
    AmountInput,
    BusinessesRow,
    GroupMembers,
    ReadonlyAmount,
    ReadonlyText,
    ReportedStatus,
    RowLabel,
    SectionCard,
    TrackingFields,
    ValuesTable,
} from './SectionParts';
import styles from '../ReportingFollowUp.module.scss';

const KEYS = MANUAL_KEYS.ADVANCE;

const AdvanceSection = ({
    advance,
    periodLabel,
    fileNumber,
    reportingType,
    partners,
    selectedPartnerId,
    onPartnerChange,
    isPartnership,
    tracking,
    onReportChange,
    onCopy,
    onReport,
    reportDisabled,
    onTrackingChange,
    onManualChange,
    direction,
}) => {
    const { t } = useTranslation();
    const partnerOptions = (partners || []).map((p) => ({ value: p.id, label: p.name }));
    const manual = tracking.manualValues || {};
    const usePercentage = advance?.settings?.usePercentage ?? true;
    const mismatch = advance?.mismatch || {};

    return (
        <SectionCard title={t('FollowUpAdvanceTitle')} fileNumber={fileNumber} reportingType={reportingType}>
            {isPartnership ? (
                <div className={styles.partnerSelect}>
                    <AutocompleteSelectFilter
                        options={partnerOptions}
                        selectedValues={partnerOptions.find((o) => o.value === selectedPartnerId) || null}
                        onChange={(e, option) => onPartnerChange(option?.value ?? null)}
                        placeholder={t('SelectPartner')}
                        isMultiple={false}
                        direction={direction}
                        portalMenu
                    />
                </div>
            ) : null}

            {advance ? (
                <>
                    <ReportedStatus isReported={advance.isReported} onReport={onReport} disabled={reportDisabled} />
                    <ValuesTable periodLabel={periodLabel} columns={[t('FollowUpBooks'), t('FollowUpReport')]}>
                        <RowLabel>{t('FollowUpBusinessTurnover')}</RowLabel>
                        <ReadonlyAmount value={advance.books.turnover} />
                        <AmountInput
                            value={advance.report.witholdingIncomesNoVat}
                            onChange={(value) => onReportChange('witholdingIncomesNoVat', value)}
                            mismatch={mismatch.turnover}
                        />

                        <RowLabel>{t('FollowUpAdvancePercentage')}</RowLabel>
                        {usePercentage ? (
                            <>
                                <ReadonlyText value={`${advance.books.percentage}%`} />
                                <AmountInput
                                    value={advance.report.incomeTaxAdvancePercentage}
                                    onChange={(value) => onReportChange('incomeTaxAdvancePercentage', value)}
                                    mismatch={mismatch.percentage}
                                />
                            </>
                        ) : (
                            <>
                                <ReadonlyText value={null} />
                                <ReadonlyText value={null} />
                            </>
                        )}

                        <RowLabel>{t('FollowUpAdvanceAmount')}</RowLabel>
                        <ReadonlyAmount value={advance.books.advanceAmount} />
                        {usePercentage ? (
                            <ReadonlyAmount value={advance.reportComputed.advanceAmount} mismatch={mismatch.advanceAmount} />
                        ) : (
                            <AmountInput
                                value={advance.report.incomeTaxAdvanceAmount}
                                onChange={(value) => onReportChange('incomeTaxAdvanceAmount', value)}
                                mismatch={mismatch.advanceAmount}
                            />
                        )}

                        <RowLabel>{t('FollowUpWithholdingInPeriod')}</RowLabel>
                        <ReadonlyAmount value={advance.books.withholding} />
                        <AmountInput
                            value={advance.report.acceptedWithholdingTax}
                            onChange={(value) => onReportChange('acceptedWithholdingTax', value)}
                            mismatch={mismatch.withholding}
                        />

                        <RowLabel>{t('FollowUpWithholdingForOffset')}</RowLabel>
                        <ReadonlyAmount value={advance.books.withholdingOffset} />
                        <ReadonlyAmount value={advance.reportComputed.withholdingOffset} mismatch={mismatch.withholdingOffset} />

                        <RowLabel>{t('FollowUpAdvanceToPay')}</RowLabel>
                        <ReadonlyAmount value={advance.books.toPay} total />
                        <ReadonlyAmount value={advance.reportComputed.toPay} total mismatch={mismatch.toPay} />

                        <BusinessesRow count={advance.businessesCount} onCopy={onCopy} />
                    </ValuesTable>
                    <GroupMembers members={advance.members} />
                </>
            ) : null}

            <TrackingFields tracking={tracking} onChange={onTrackingChange} direction={direction} />

            <ValuesTable periodLabel={periodLabel} columns={[t('FollowUpBooks'), t('FollowUpReport')]}>
                <RowLabel>
                    <span title={t('FollowUpPriorExcessDeductionsHint')}>{t('FollowUpPriorExcessDeductions')}</span>
                </RowLabel>
                {advance?.priorExcessLoading ? (
                    <>
                        <ReadonlyText value={t('Loading')} />
                        <ReadonlyText value={t('Loading')} />
                    </>
                ) : (
                    <>
                        <ReadonlyAmount value={advance ? advance.books.priorExcessDeductions : null} />
                        <ReadonlyAmount
                            value={advance ? advance.reportComputed.priorExcessDeductions : null}
                            mismatch={mismatch.priorExcessDeductions}
                        />
                    </>
                )}

                <RowLabel>{t('FollowUpPeriodExcessDeductions')}</RowLabel>
                <ReadonlyAmount value={advance ? advance.books.periodExcessDeductions : null} />
                <ReadonlyAmount
                    value={advance?.hasReport ? advance.reportComputed.periodExcessDeductions : null}
                    mismatch={mismatch.periodExcessDeductions}
                />

                <RowLabel>{t('FollowUpExcessDeductionsNextReport')}</RowLabel>
                <ReadonlyAmount value={advance ? advance.books.excessDeductions : null} total />
                <ReadonlyAmount
                    value={advance?.hasReport ? advance.reportComputed.excessDeductions : null}
                    total
                    mismatch={mismatch.excessDeductions}
                />

                <RowLabel>{t('FollowUpExcessPaymentsNextReport')}</RowLabel>
                <AmountInput
                    value={manual[KEYS.excessPaymentsBooks]}
                    onChange={(value) => onManualChange(KEYS.excessPaymentsBooks, value)}
                />
                <AmountInput
                    value={manual[KEYS.excessPaymentsReport]}
                    onChange={(value) => onManualChange(KEYS.excessPaymentsReport, value)}
                />
            </ValuesTable>
        </SectionCard>
    );
};

AdvanceSection.propTypes = {
    advance: PropTypes.object,
    periodLabel: PropTypes.string,
    fileNumber: PropTypes.string,
    reportingType: PropTypes.string,
    partners: PropTypes.array,
    selectedPartnerId: PropTypes.string,
    onPartnerChange: PropTypes.func.isRequired,
    isPartnership: PropTypes.bool,
    tracking: PropTypes.object.isRequired,
    onReportChange: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
    onReport: PropTypes.func,
    reportDisabled: PropTypes.bool,
    onTrackingChange: PropTypes.func.isRequired,
    onManualChange: PropTypes.func.isRequired,
    direction: PropTypes.string,
};

export default AdvanceSection;
